import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { socialApi } from "@/lib/socialApi";
import YouTubeVideoCard from "./YouTubeVideoCard";

const HOME_VIDEO_LIMIT = 8;

const LatestVideosSection = () => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['youtube-videos', HOME_VIDEO_LIMIT],
        queryFn: async () => {
            const res = await socialApi.getYouTubeVideos(HOME_VIDEO_LIMIT);
            return res.data.data;
        },
        staleTime: 30 * 60 * 1000,
        retry: 1,
    });

    const videos = data?.videos ?? [];
    const channelUrl = data?.channelUrl ?? 'https://www.youtube.com/channel/UC2h0v_PBUQzODLNT9EIUGzw';

    if (!isLoading && !isError && videos.length === 0) {
        return null;
    }

    return (
        <section className="py-32 bg-white/[0.01] border-y border-white/5" dir="rtl">
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-white/60 mb-6">
                            <Youtube size={14} className="text-primary" />
                            أحدث الفيديوهات
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-normal" style={{ lineHeight: '1.6' }}>
                            أحدث الشورتس <br />
                            <span className="text-muted-foreground">على يوتيوب.</span>
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                            لقطات قصيرة من مشاريعنا، نصائح تقنية، ومحتوى سريع نشاركه أولاً بأول.
                        </p>
                    </div>
                    <div className="flex gap-4">
                        <a href={channelUrl} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" className="rounded-full border-white/10 hover:bg-white/5 text-white font-bold gap-2">
                                <Youtube size={18} />
                                القناة على يوتيوب
                            </Button>
                        </a>
                        <Link to="/videos">
                            <Button className="rounded-full bg-primary text-white font-bold gap-2">
                                عرض جميع الفيديوهات
                                <ArrowLeft size={18} />
                            </Button>
                        </Link>
                    </div>
                </div>

                {isLoading && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {Array.from({ length: HOME_VIDEO_LIMIT }).map((_, i) => (
                            <div
                                key={i}
                                className="bento-card overflow-hidden animate-pulse"
                            >
                                <div className="aspect-[9/16] bg-white/5" />
                                <div className="p-6 space-y-3">
                                    <div className="h-5 bg-white/5 rounded w-3/4" />
                                    <div className="h-3 bg-white/5 rounded w-1/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!isLoading && isError && (
                    <div className="text-center py-16">
                        <p className="text-muted-foreground font-medium">
                            تعذّر تحميل الفيديوهات حالياً. تفضّل بزيارة قناتنا مباشرة.
                        </p>
                    </div>
                )}

                {!isLoading && !isError && videos.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {videos.map((video) => (
                            <YouTubeVideoCard key={video.id} video={video} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default LatestVideosSection;
